import type {
  BoundaryFrame,
  SolidFootprintOptions,
  Vec3,
  VoxelIJKChange,
  VoxelIJKCollection,
  LabelDataApplyVoxelCollection,
} from './markerLineTypes';
import { clampValue, crossVec3, normalizeVec3 } from './markerLineUtils';

export type LocalDeformConfig = {
  radiusRows: number;
  sigmaRows: number;
  minThickness: number;
  smoothRadiusRows: number;
};

export const getImageSpacingMm = (labelData: any): [number, number, number] => {
  const spacing = labelData?.getSpacing?.() as [number, number, number] | undefined;
  return [
    Number(spacing?.[0]) > 0 ? Number(spacing?.[0]) : 1,
    Number(spacing?.[1]) > 0 ? Number(spacing?.[1]) : 1,
    Number(spacing?.[2]) > 0 ? Number(spacing?.[2]) : 1,
  ];
};

export const maskIndexToWorldPoint = (
  idx: number,
  frame: BoundaryFrame,
): { world: Vec3; rowFrame: BoundaryFrame['rows'][number] } | null => {
  const row = Math.floor(idx / frame.width);
  const col = idx - row * frame.width;
  const rowFrame = frame.rows[row];
  if (!rowFrame) return null;
  const xMM = ((col + 0.5) / Math.max(1, frame.width) - 0.5) * frame.widthMM;
  return {
    rowFrame,
    world: [
      rowFrame.center[0] + xMM * rowFrame.direction[0],
      rowFrame.center[1] + xMM * rowFrame.direction[1],
      rowFrame.center[2] + xMM * rowFrame.direction[2],
    ],
  };
};

export const worldPointToVoxelIJK = (
  world: Vec3,
  value: number,
  worldToIndex: ArrayLike<number>,
  dims: [number, number, number],
  weight?: number,
  clampToBounds = true,
): VoxelIJKChange | null => {
  const w =
    worldToIndex[3] * world[0] +
    worldToIndex[7] * world[1] +
    worldToIndex[11] * world[2] +
    worldToIndex[15];
  const invW = w && Number.isFinite(w) ? 1 / w : 1;
  const iRaw =
    (worldToIndex[0] * world[0] +
      worldToIndex[4] * world[1] +
      worldToIndex[8] * world[2] +
      worldToIndex[12]) *
    invW;
  const jRaw =
    (worldToIndex[1] * world[0] +
      worldToIndex[5] * world[1] +
      worldToIndex[9] * world[2] +
      worldToIndex[13]) *
    invW;
  const kRaw =
    (worldToIndex[2] * world[0] +
      worldToIndex[6] * world[1] +
      worldToIndex[10] * world[2] +
      worldToIndex[14]) *
    invW;

  if (![iRaw, jRaw, kRaw].every(Number.isFinite)) return null;

  const i = Math.round(iRaw);
  const j = Math.round(jRaw);
  const k = Math.round(kRaw);

  if (!clampToBounds) {
    if (i < 0 || j < 0 || k < 0 || i >= dims[0] || j >= dims[1] || k >= dims[2]) {
      return null;
    }
    return {
      i,
      j,
      k,
      value,
      ...(weight !== undefined ? { weight } : {}),
    };
  }

  return {
    i: Math.max(0, Math.min(dims[0] - 1, i)),
    j: Math.max(0, Math.min(dims[1] - 1, j)),
    k: Math.max(0, Math.min(dims[2] - 1, k)),
    value,
    ...(weight !== undefined ? { weight } : {}),
  };
};

export const maskIndexToVoxelIJK = (
  idx: number,
  value: number,
  frame: BoundaryFrame,
  worldToIndex: ArrayLike<number>,
  dims: [number, number, number],
): VoxelIJKChange | null => {
  const point = maskIndexToWorldPoint(idx, frame);
  if (!point) return null;
  return worldPointToVoxelIJK(point.world, value, worldToIndex, dims);
};

export const estimateSupportInteriorDistancePx = (
  idx: number,
  support: Set<number>,
  frame: BoundaryFrame,
  maxRadius: number,
) => {
  const row = Math.floor(idx / frame.width);
  const col = idx - row * frame.width;
  const height = frame.rows.length;

  for (let radius = 1; radius <= maxRadius; radius += 1) {
    for (let y = row - radius; y <= row + radius; y += 1) {
      for (let x = col - radius; x <= col + radius; x += 1) {
        if (
          y !== row - radius &&
          y !== row + radius &&
          x !== col - radius &&
          x !== col + radius
        ) {
          continue;
        }
        if (x < 0 || x >= frame.width || y < 0 || y >= height) {
          return radius - 1;
        }
        if (!support.has(y * frame.width + x)) {
          return radius - 1;
        }
      }
    }
  }

  return maxRadius;
};

export const get3DVoxelExpansionConfig = (
  frame: BoundaryFrame,
  imageSpacingMm: [number, number, number],
  localDeformConfig: LocalDeformConfig,
) => {
  const minVoxelSpacingMm = Math.min(
    imageSpacingMm[0],
    imageSpacingMm[1],
    imageSpacingMm[2],
  );
  const sampleStepMm = clampValue(
    Math.min(minVoxelSpacingMm, frame.spacingX),
    0.35,
    0.75,
  );
  const radialRadiusMm = clampValue(frame.spacingX * 2.0, 0.7, 1.6);
  const maxDepthRadiusMm = clampValue(
    frame.spacingY * localDeformConfig.sigmaRows,
    1.5,
    4.0,
  );

  return {
    radialRadiusMm,
    maxDepthRadiusMm,
    sampleStepMm,
    minWeight: 0.18,
    minDepthScale: 0.65,
    edgeDistancePx: localDeformConfig.radiusRows,
    sigmaRadialMm: Math.max(0.25, radialRadiusMm * 0.55),
    sigmaDepthMm: Math.max(0.45, maxDepthRadiusMm * 0.5),
  };
};

export const collectVoxelIJK = ({
  maskIndices,
  valueForIndex,
  frame,
  labelData,
  localDeformConfig,
}: {
  maskIndices: Iterable<number>;
  valueForIndex: (idx: number) => number;
  frame: BoundaryFrame | null | undefined;
  labelData: any;
  localDeformConfig: LocalDeformConfig;
}): VoxelIJKCollection => {
  const startedAt = performance.now();
  const worldToIndex = labelData?.getWorldToIndex?.() as ArrayLike<number> | undefined;
  const dims = labelData?.getDimensions?.() as [number, number, number] | undefined;
  const indices = Array.from(maskIndices);
  const fallbackConfig = {
    radialRadiusMm: 0,
    maxDepthRadiusMm: 0,
    sampleStepMm: 0,
    minWeight: 0,
    edgeDistancePx: 0,
  };

  if (!frame || !worldToIndex || !dims) {
    return {
      flatVoxelIJK: [],
      voxelIJK3D: [],
      elapsedMs: performance.now() - startedAt,
      sourcePointCount: indices.length,
      config: fallbackConfig,
    };
  }

  const flatVoxelIJK: VoxelIJKChange[] = [];
  const expanded = new Map<string, VoxelIJKChange>();
  const support = new Set(indices);
  const cfg = get3DVoxelExpansionConfig(
    frame,
    getImageSpacingMm(labelData),
    localDeformConfig,
  );
  const radialSteps = Math.max(0, Math.ceil(cfg.radialRadiusMm / cfg.sampleStepMm));

  for (const idx of indices) {
    const value = valueForIndex(idx);
    const voxel = maskIndexToVoxelIJK(idx, value, frame, worldToIndex, dims);
    if (voxel) flatVoxelIJK.push(voxel);

    const point = maskIndexToWorldPoint(idx, frame);
    if (!point) continue;

    const interiorPx = estimateSupportInteriorDistancePx(
      idx,
      support,
      frame,
      cfg.edgeDistancePx,
    );
    const depthScale =
      cfg.minDepthScale +
      (1 - cfg.minDepthScale) * (interiorPx / cfg.edgeDistancePx);
    const depthRadiusMm = cfg.maxDepthRadiusMm * depthScale;
    const sigmaDepthMm = cfg.sigmaDepthMm * depthScale;
    const depthSteps = Math.max(0, Math.ceil(depthRadiusMm / cfg.sampleStepMm));
    const radialAxis = normalizeVec3(point.rowFrame.direction, [1, 0, 0]);
    const depthAxis = normalizeVec3(point.rowFrame.slabAxis ?? [0, 0, 1], [0, 0, 1]);

    for (let ri = -radialSteps; ri <= radialSteps; ri += 1) {
      const radialMm = ri * cfg.sampleStepMm;
      if (Math.abs(radialMm) > cfg.radialRadiusMm + 1e-6) continue;
      for (let di = -depthSteps; di <= depthSteps; di += 1) {
        const depthMm = di * cfg.sampleStepMm;
        if (Math.abs(depthMm) > depthRadiusMm + 1e-6) continue;

        const radialNorm =
          (radialMm * radialMm) / (2 * cfg.sigmaRadialMm * cfg.sigmaRadialMm);
        const depthNorm =
          (depthMm * depthMm) / (2 * sigmaDepthMm * sigmaDepthMm);
        const weight = Math.exp(-(radialNorm + depthNorm));
        if (weight < cfg.minWeight) continue;

        const world: Vec3 = [
          point.world[0] + radialMm * radialAxis[0] + depthMm * depthAxis[0],
          point.world[1] + radialMm * radialAxis[1] + depthMm * depthAxis[1],
          point.world[2] + radialMm * radialAxis[2] + depthMm * depthAxis[2],
        ];
        const expandedVoxel = worldPointToVoxelIJK(
          world,
          value,
          worldToIndex,
          dims,
          Math.round(weight * 1000) / 1000,
        );
        if (!expandedVoxel) continue;

        const key = `${expandedVoxel.i}:${expandedVoxel.j}:${expandedVoxel.k}:${value}`;
        const prev = expanded.get(key);
        if (!prev || (expandedVoxel.weight ?? 0) > (prev.weight ?? 0)) {
          expanded.set(key, expandedVoxel);
        }
      }
    }
  }

  return {
    flatVoxelIJK,
    voxelIJK3D: Array.from(expanded.values()),
    elapsedMs: performance.now() - startedAt,
    sourcePointCount: indices.length,
    config: {
      radialRadiusMm: cfg.radialRadiusMm,
      maxDepthRadiusMm: cfg.maxDepthRadiusMm,
      sampleStepMm: cfg.sampleStepMm,
      minWeight: cfg.minWeight,
      edgeDistancePx: cfg.edgeDistancePx,
    },
  };
};

export const getBoundaryRowAxis = (
  row: number,
  frame: BoundaryFrame,
  rowFrame: BoundaryFrame['rows'][number],
): Vec3 => {
  const prev = frame.rows[Math.max(0, row - 1)]?.center;
  const next = frame.rows[Math.min(frame.rows.length - 1, row + 1)]?.center;
  if (prev && next) {
    return normalizeVec3(
      [next[0] - prev[0], next[1] - prev[1], next[2] - prev[2]],
      normalizeVec3(
        crossVec3(rowFrame.slabAxis ?? [0, 0, 1], rowFrame.direction),
        [0, 1, 0],
      ),
    );
  }
  return normalizeVec3(
    crossVec3(rowFrame.slabAxis ?? [0, 0, 1], rowFrame.direction),
    [0, 1, 0],
  );
};

export const collectSolidFootprintVoxelIJK = ({
  maskIndices,
  valueForIndex,
  frame,
  labelData,
  options = {},
}: {
  maskIndices: Iterable<number>;
  valueForIndex: (idx: number) => number;
  frame: BoundaryFrame | null | undefined;
  labelData: any;
  options?: SolidFootprintOptions;
}): LabelDataApplyVoxelCollection => {
  const startedAt = performance.now();
  const worldToIndex = labelData?.getWorldToIndex?.() as ArrayLike<number> | undefined;
  const dims = labelData?.getDimensions?.() as [number, number, number] | undefined;
  const fallback = {
    voxelIJK: [],
    elapsedMs: performance.now() - startedAt,
    config: {
      method: 'solid-footprint',
      radiusUMm: 0,
      radiusVMm: 0,
      depthRadiusMm: 0,
      sampleStepMm: 0,
    },
  };
  if (!frame || !worldToIndex || !dims) return fallback;

  const spacing = getImageSpacingMm(labelData);
  const minVoxelSpacingMm = Math.min(spacing[0], spacing[1], spacing[2]);
  const stepMm = clampValue(
    Math.min(minVoxelSpacingMm * 0.5, frame.spacingX * 0.5, frame.spacingY * 0.5),
    0.18,
    0.45,
  );
  const radiusScale = options.radiusScale ?? 0.45;
  const depthScale = options.depthScale ?? 0.45;
  const radiusUMm = Math.max(frame.spacingX * radiusScale, stepMm * 0.75);
  const radiusVMm = Math.max(frame.spacingY * radiusScale, stepMm * 0.75);
  const depthRadiusMm = Math.max(minVoxelSpacingMm * depthScale, stepMm * 0.75);
  const uSteps = Math.max(0, Math.ceil(radiusUMm / stepMm));
  const vSteps = Math.max(0, Math.ceil(radiusVMm / stepMm));
  const depthSteps = Math.max(0, Math.ceil(depthRadiusMm / stepMm));
  const out = new Map<string, VoxelIJKChange>();

  for (const idx of maskIndices) {
    const value = valueForIndex(idx);
    const row = Math.floor(idx / frame.width);
    const point = maskIndexToWorldPoint(idx, frame);
    if (!point) continue;
    const uAxis = normalizeVec3(point.rowFrame.direction, [1, 0, 0]);
    const vAxis = getBoundaryRowAxis(row, frame, point.rowFrame);
    const dAxis = normalizeVec3(crossVec3(uAxis, vAxis), [
      point.rowFrame.slabAxis?.[0] ?? 0,
      point.rowFrame.slabAxis?.[1] ?? 0,
      point.rowFrame.slabAxis?.[2] ?? 1,
    ]);

    for (let ui = -uSteps; ui <= uSteps; ui += 1) {
      const uMm = ui * stepMm;
      if (Math.abs(uMm) > radiusUMm + 1e-6) continue;
      for (let vi = -vSteps; vi <= vSteps; vi += 1) {
        const vMm = vi * stepMm;
        if (Math.abs(vMm) > radiusVMm + 1e-6) continue;
        for (let di = -depthSteps; di <= depthSteps; di += 1) {
          const depthMm = di * stepMm;
          if (Math.abs(depthMm) > depthRadiusMm + 1e-6) continue;
          const world: Vec3 = [
            point.world[0] + uMm * uAxis[0] + vMm * vAxis[0] + depthMm * dAxis[0],
            point.world[1] + uMm * uAxis[1] + vMm * vAxis[1] + depthMm * dAxis[1],
            point.world[2] + uMm * uAxis[2] + vMm * vAxis[2] + depthMm * dAxis[2],
          ];
          const voxel = worldPointToVoxelIJK(world, value, worldToIndex, dims, undefined, false);
          if (!voxel) continue;
          out.set(`${voxel.i}:${voxel.j}:${voxel.k}:${value}`, voxel);
        }
      }
    }
  }

  return {
    voxelIJK: Array.from(out.values()),
    elapsedMs: performance.now() - startedAt,
    config: {
      method: 'solid-footprint',
      radiusUMm,
      radiusVMm,
      depthRadiusMm,
      sampleStepMm: stepMm,
    },
  };
};

export const expandVoxelIJKForLabelApply = ({
  source,
  value,
  labelData,
  radiusVoxels = 1,
}: {
  source: VoxelIJKChange[];
  value: number;
  labelData: any;
  radiusVoxels?: number;
}): LabelDataApplyVoxelCollection => {
  const startedAt = performance.now();
  const dims = labelData?.getDimensions?.() as [number, number, number] | undefined;
  const out = new Map<string, VoxelIJKChange>();

  if (!dims || !source.length) {
    return {
      voxelIJK: [],
      elapsedMs: performance.now() - startedAt,
      config: {
        method: 'logged-3d-ijk-dilation',
        radiusVoxels,
      },
    };
  }

  const radius = Math.max(0, Math.floor(radiusVoxels));
  for (const base of source) {
    for (let dk = -radius; dk <= radius; dk += 1) {
      for (let dj = -radius; dj <= radius; dj += 1) {
        for (let di = -radius; di <= radius; di += 1) {
          const i = base.i + di;
          const j = base.j + dj;
          const k = base.k + dk;
          if (i < 0 || j < 0 || k < 0 || i >= dims[0] || j >= dims[1] || k >= dims[2]) {
            continue;
          }
          out.set(`${i}:${j}:${k}:${value}`, { i, j, k, value });
        }
      }
    }
  }

  return {
    voxelIJK: Array.from(out.values()),
    elapsedMs: performance.now() - startedAt,
    config: {
      method: 'logged-3d-ijk-dilation',
      radiusVoxels: radius,
    },
  };
};

export const collectLocalCurrentLumenIndices = (
  afterMask: Uint8Array,
  changed: Iterable<number>,
  frame: BoundaryFrame | null | undefined,
  predicate: (isLumen: boolean) => boolean = (isLumen) => isLumen,
) => {
  if (!frame) return new Set<number>();

  const indices = Array.from(changed);
  if (!indices.length) return new Set<number>();

  const padding = 3;
  let minRow = Number.POSITIVE_INFINITY;
  let maxRow = Number.NEGATIVE_INFINITY;
  let minCol = Number.POSITIVE_INFINITY;
  let maxCol = Number.NEGATIVE_INFINITY;

  for (const idx of indices) {
    const row = Math.floor(idx / frame.width);
    const col = idx - row * frame.width;
    minRow = Math.min(minRow, row);
    maxRow = Math.max(maxRow, row);
    minCol = Math.min(minCol, col);
    maxCol = Math.max(maxCol, col);
  }

  const out = new Set<number>();
  const rowStart = Math.max(0, minRow - padding);
  const rowEnd = Math.min(frame.rows.length - 1, maxRow + padding);
  const colStart = Math.max(0, minCol - padding);
  const colEnd = Math.min(frame.width - 1, maxCol + padding);

  for (let row = rowStart; row <= rowEnd; row += 1) {
    const base = row * frame.width;
    for (let col = colStart; col <= colEnd; col += 1) {
      const idx = base + col;
      if (predicate(afterMask[idx] > 0)) out.add(idx);
    }
  }
  return out;
};
